import React, { useCallback, useMemo, useState } from 'react';

import { Chip, Typography } from '@mui/material';

import ProtectedData, { isProtectedData, LockIconBadge } from 'pages/item/v2/components/ProtectedData';
import ReferenceGroup from 'pages/item/v2/components/Related/components/ReferenceGroup';
import isEmpty from 'utils/is-empty';

import useStyles from './styles';

interface Props {
    references: {
        [type: string]: {
            id: string;
            title: string;
            published: string;
            bulletinFamily: string;
        }[];
    };
    className?: string;
}

const Related: React.FC<Props> = ({ references, className }) => {
    const { classes, cx } = useStyles();
    const [filteredTags, setFilteredTags] = useState<Set<string>>(new Set());

    const tagsDictionary = useMemo(
        () =>
            Object.values(references).reduce((output, items) => {
                const [{ bulletinFamily }] = items;
                output[bulletinFamily] ||= 0;
                output[bulletinFamily] += items.length;
                return output;
            }, {}),
        [references]
    );

    const getHandleTagClick = useCallback(
        (tag) => () =>
            setFilteredTags((state) => {
                state = new Set(state);
                state.has(tag) ? state.delete(tag) : state.add(tag);
                return state;
            }),
        []
    );

    const groups = useMemo(
        () =>
            Object.entries(references).filter(
                ([_type, [{ bulletinFamily }]]) => isEmpty(filteredTags) || filteredTags.has(bulletinFamily)
            ),
        [filteredTags, references]
    );

    const hasProtectedData = useMemo(
        () =>
            Object.values(references)
                .flat()
                .map((item) => Object.values(item))
                .flat()
                .some((value) => isProtectedData(value)),
        [references]
    );

    return (
        <div className={cx(classes.root, className)}>
            <Typography variant="h3" className={classes.title}>
                <LockIconBadge invisible={!hasProtectedData}>{'Related'}</LockIconBadge>
            </Typography>
            <div>
                {Object.entries(tagsDictionary).map(([tag, count], index) => (
                    <ProtectedData data={tag} key={index}>
                        {({ isProtected, content }) => (
                            <Chip
                                classes={{
                                    root: classes.chip,
                                    label: classes.label,
                                    colorPrimary: classes.primary
                                }}
                                color={filteredTags.has(tag) ? 'primary' : 'default'}
                                variant={filteredTags.has(tag) ? 'filled' : 'outlined'}
                                label={isProtected ? content : `${tag} ${count}`}
                                onClick={isProtected ? undefined : getHandleTagClick(tag)}
                            />
                        )}
                    </ProtectedData>
                ))}
            </div>
            {groups.map(([type, items], index) => (
                <ReferenceGroup key={index} type={type} items={items} />
            ))}
        </div>
    );
};

export default Related;
